import { of } from "rxjs";
import { catchError, filter, ignoreElements, map, mergeMap, tap } from "rxjs/operators";
import { StateObservable } from "redux-observable";
import { ActionType } from "typesafe-actions";

import * as navigationService from "../../../features/navigation/services/navigation.service";
import { Effect } from "../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../redux/types/effect-actions-observable.type";
import { HIDE_NEW_CAMPAIGN_BRIEF_MODAL } from "../../../constants/types";
import { RootState } from "../../../redux/store";
import { addMessage } from "../../../redux/actions/messagesActions";
import { getReviewStages } from "../../../redux/actions/reviewStagesActions";
import { isActionType } from "../../../redux/action.functions";

import * as campaignBriefService from "../services/campaign-brief.service";
import * as fileAttachmentService from "../services/campaign-brief-file-attachment.service";
import { CampaignBriefProductsEffects } from "../features/campaign-brief-products/store/campaign-brief-products.effects";
import {
  shouldResetBrief,
  shouldStartReview,
} from "../types/campaign-brief-form/campaign-brief-form.functions";

import * as actions from "./campaign-brief.actions";
import * as store from "./campaign-brief.store";
import { CampaignBriefActions } from "./campaign-brief.actions";

const isActionTypeWithErrorStatus = (type: string, status: number) => (
  action: ActionType<any>
): boolean => action.type === type && action.payload?.status === status;

const fetch: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Fetch)),
    map((action) => action.payload),
    mergeMap((id) =>
      campaignBriefService.fetchBrief(id).pipe(
        map((brief) => actions.fetchSuccess(brief)),
        catchError((error) => of(actions.fetchFailure(error)))
      )
    )
  );

const fetchSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.FetchSuccess)),
    map((action) => action.payload),
    tap((brief) => getReviewStages(brief.id, "campaign-briefs")),
    map(() => actions.fetchAttachedFiles())
  );

const create: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Create)),
    map((action) => action.payload),
    mergeMap((form) =>
      campaignBriefService.create(form).pipe(
        map((result) => actions.createSuccess(result)),
        catchError((error) => of(actions.createFailure(error)))
      )
    )
  );

const createSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.CreateSuccess)),
    mergeMap(() => of({ type: HIDE_NEW_CAMPAIGN_BRIEF_MODAL }))
  );

const reset: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.ResetBrief)),
    map((action) => action.payload),
    mergeMap((form) =>
      campaignBriefService.resetBrief(form.id).pipe(
        map((result) => actions.resetSuccess(result, form)),
        catchError((error) => of(actions.resetFailure(error)))
      )
    )
  );

const resetSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.ResetSuccess)),
    map((action) => action.payload),
    mergeMap(({ form, result }) =>
      of(
        addMessage({ text: "Successfully restarted campaign.", severity: "success" }),
        actions.update(form)
      )
    )
  );

const save: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Save)),
    map((action) => ({
      form: action.payload,
      currentState: store.getBrief(state$.value),
    })),
    map(({ form, currentState }) =>
      shouldResetBrief(currentState, form) ? actions.resetBrief(form) : actions.update(form)
    )
  );

const rename: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Rename)),
    map((action) => action.payload),
    mergeMap((form) =>
      campaignBriefService.renameBrief(form).pipe(
        map((result) => actions.renameSuccess(result)),
        catchError((error) => of(actions.renameFailure(error)))
      )
    )
  );

const update: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Update)),
    map((action) => action.payload),
    mergeMap((form) =>
      campaignBriefService.updateBrief(form).pipe(
        map((result) => actions.updateSuccess(result, form)),
        catchError((error) => of(actions.updateFailure(error)))
      )
    )
  );

const updateFailure: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionTypeWithErrorStatus(CampaignBriefActions.UpdateFailure, 400)),
    map((action) => action.payload),
    map(({ errorMessage }) => addMessage({ text: errorMessage, severity: "error" }))
  );

const updateSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.UpdateSuccess)),
    map((action) => action.payload),
    map(({ form, result }) =>
      shouldStartReview(result, form)
        ? actions.startReview(form.id)
        : addMessage({ text: "Successfully saved campaign.", severity: "success" })
    )
  );

const startReview: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.StartReview)),
    map((action) => action.payload),
    mergeMap((id) =>
      campaignBriefService.startReview(id).pipe(
        map(() => actions.startReviewSuccess()),
        catchError((error) => of(actions.startReviewFailure(error)))
      )
    )
  );

const startReviewSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.StartReviewSuccess)),
    map(() => addMessage({ text: "Successfully started review process.", severity: "success" }))
  );

const search: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Search)),
    map((action) => action.payload),
    mergeMap((searchTerm) =>
      campaignBriefService.search(searchTerm).pipe(
        map((results) => actions.searchSuccess(results)),
        catchError((error) => of(actions.searchFailure(error)))
      )
    )
  );

const duplicate: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.Duplicate)),
    map((action) => action.payload),
    mergeMap((form) =>
      campaignBriefService.duplicate(form).pipe(
        map((result) => actions.duplicateSuccess(result)),
        catchError((error) => of(actions.duplicateFailure(error)))
      )
    )
  );

const newBriefRedirect: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(
      (action) =>
        isActionType(CampaignBriefActions.DuplicateSuccess)(action) ||
        isActionType(CampaignBriefActions.CreateSuccess)(action)
    ),
    map((action) => action.payload),
    tap((brief) => navigationService.goToCampaignBrief(brief.id)),
    ignoreElements()
  );

const removeAttachedFile: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.RemoveAttachedFile)),
    map((action) => ({
      attachmentId: action.payload,
      brief: store.getBrief(state$.value),
    })),
    mergeMap(({ brief, attachmentId }) =>
      fileAttachmentService.deleteAttachment(brief, attachmentId).pipe(
        map((result) => actions.removeAttachedFileSuccess(result)),
        catchError((error) => of(actions.removeAttachedFileFailure(error)))
      )
    )
  );

const fetchAttachedFiles: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.FetchAttachedFiles)),
    map(() => store.getBriefId(state$.value)),
    mergeMap((briefId) =>
      fileAttachmentService.fetchAttachments(briefId).pipe(
        map((attachments) => actions.fetchAttachedFilesSuccess(attachments)),
        catchError((error) => of(actions.fetchAttachedFilesFailure(error)))
      )
    )
  );

const fetchAttachedFilesSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.FetchAttachedFilesSuccess)),
    map((action) => action.payload),
    map(actions.setAttachedFiles)
  );

const uploadAttachedFile: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.UploadAttachmentFile)),
    map((action) => action.payload),
    mergeMap((payload) =>
      fileAttachmentService.addAttachment(payload.briefId, payload.file).pipe(
        map((file) => actions.uploadAttachedFileSuccess(file)),
        catchError((error) => of(actions.uploadAttachedFileFailure(error)))
      )
    )
  );

const uploadAttachedFileSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(CampaignBriefActions.UploadAttachmentFileSuccess)),
    map((action) => ({
      attachment: action.payload,
      attachedFiles: store.getAttachedFiles(state$.value),
    })),
    map(({ attachment, attachedFiles }) =>
      actions.setAttachedFiles(fileAttachmentService.addAttachedFile(attachment, attachedFiles))
    )
  );

export const CampaignBriefEffects = [
  create,
  createSuccess,
  fetch,
  fetchSuccess,
  rename,
  reset,
  resetSuccess,
  update,
  updateFailure,
  updateSuccess,
  startReview,
  startReviewSuccess,
  save,
  search,
  duplicate,
  newBriefRedirect,
  removeAttachedFile,
  fetchAttachedFiles,
  fetchAttachedFilesSuccess,
  uploadAttachedFile,
  uploadAttachedFileSuccess,
  ...CampaignBriefProductsEffects,
];
